import { CommonModule, NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdaptIDJobPostingSchema, JobListSchema } from '@app/schemas/jobs';
import { UnderscoreToLowerPipe } from '@app/utils/string-transform.pipe';
import { TimeAgoPipe } from '@app/utils/time-difference.pipe';
import { Store } from '@ngrx/store';
import { CATEGORY_ICON_MAP } from '@app/utils/find-job';
import { analytics } from '@app/events/segment/analytics';
import { setJobList } from '@app/state/actions/jobs.action';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningSnackBarComponent } from '@app/shared/snack-bars/warning/warning.component';
import { WarningDefaultOptions } from '@app/shared/snack-bars/warning/warning.utils';
import { UserJobBookmarkService } from '@app/services/api/job-bookmark/user-job-bookmark.service';
import { finalize } from 'rxjs';
import { updateLoaderState } from '@app/state/actions/global.action';
import { JobFilterFormDataSchema } from '@app/schemas/job-filter';

@Component({
  selector: 'app-job-item',
  imports: [
    MatIconModule,
    CommonModule,
    MatTooltipModule,
    UnderscoreToLowerPipe,
    TimeAgoPipe,
    NgClass
  ],
  templateUrl: './job-item.component.html',
  styleUrl: './job-item.component.css'
})
export class JobItemComponent {
  @Output() jobClickEmitter = new EventEmitter<string>();
  @Input() isFilterTabOpen: boolean = false;
  @ViewChildren('jobItem') jobItemElements!: QueryList<ElementRef>;
  isCareerGoalSet: boolean = true;
  public jobListData: JobListSchema[] = [];
  allJobsData: JobListSchema[] = [];
  hasMoreItems: boolean = true;
  itemsPerPage: number = 20;
  currentPage: number = 1;
  currentJobIndex: number = 0;
  private _snackBar = inject(MatSnackBar);
  private adaptIDJobs: AdaptIDJobPostingSchema[] = [];
  private bookmarkDisabledMap = new Map<string, boolean>();

  isBookmarkDisabled(jobId: string): boolean {
    return this.bookmarkDisabledMap.get(jobId) || false;
  }

  constructor(
    private jobBookmarkService: UserJobBookmarkService,
    private store: Store<{ 
      jobList: JobListSchema[],
       adaptIdJobList: AdaptIDJobPostingSchema[], 
       careerGoal: string, 
       jobFilter: JobFilterFormDataSchema ,
       jobSortedBy: string,
    }>
  ) {
    this.store.select('jobList').subscribe(value => {
      const previousIds = this.allJobsData.map(j => j.posting_id);
      const newIds = value.map(j => j.posting_id);
      const isStructuralChange = previousIds.length !== newIds.length ||
        previousIds.some((id, i) => id !== newIds[i]);

      this.allJobsData = value;

      if (isStructuralChange) {
        this.initialJobLoad(false);
      } else {
        // Refresh displayed items in place without resetting pagination
        this.jobListData = this.allJobsData.slice(0, this.currentPage * this.itemsPerPage);
      }
    });
    this.store.select('adaptIdJobList').subscribe(value => this.adaptIDJobs = value);
    this.store.select('careerGoal').subscribe((value) => {
      this.isCareerGoalSet = value ? true : false;
    });
    this.store.select('jobFilter').subscribe(() => {
      this.sendInitialLoadJobEvent()
    });

    this.store.select('jobSortedBy').subscribe(value => {
       this.sendInitialLoadJobEvent();
    });

    this.sendInitialLoadJobEvent();
  }
  
  private sendInitialLoadJobEvent() {
    // to ensure the event is emitted after the jobs are available
    // setTimeout  is used to ensure job-description component is loaded to receive the event
    this.currentJobIndex = 0;
    setTimeout(() => {
      const job = this.jobListData[0] || null;
      if (job) {
        this.jobClickEmitter.emit(job.posting_id);
      }
    },0);
  }

  private normalizeCategory(category: string): string {
    return  category?.trim()?.toLowerCase().split(" ").slice(0, -1).join("_") || '';
  }

  getMatchIndicatorIcon(category: string): string {
    const key = this.normalizeCategory(category);
    return CATEGORY_ICON_MAP[key];
  }

  trackJobClickEvent(index: number) {
    const job = this.jobListData[index];
    if (job) {
      analytics.track('Job View', {
        section: 'job',
        job_id: job.posting_id || null,
        job_title: job.job_title || null,
        bookmarked: job.is_bookmarked ?? null,
        overall_match_level: job.score_category || null,
        skills_match_level: job?.match_summary['skills_category'] || null,
        education_match_level: job?.match_summary['education_category'] || null,
        work_history_match_level: job?.match_summary['work_history_category'] || null,
        goal_related: job.related_to_career_goal,
        aligned_skills: job.skills || [],
        skill_gap: job.skill_gaps || [],
        match_score: job.match_score || undefined,
        employer_name: job.employer_name || null,
      });
    }
  }

  onFocusJobItem(i: number) {
    this.currentJobIndex = i;
    const job = this.jobListData[i];
    if (job) {
      this.jobClickEmitter.emit(job.posting_id);
    }
  }

  onKeydown(event: KeyboardEvent, i: number) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusJobItem(i + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusJobItem(i - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onClickJobItem(i);
    }
  }

  private focusJobItem(index: number) {
    if (index >= 0 && index < this.jobListData.length) {
      const elements = this.jobItemElements.toArray();
      if (elements[index]) {
        elements[index].nativeElement.focus();
        elements[index].nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  onClickJobItem(i: number) {
    this.currentJobIndex = i;
    this.trackJobClickEvent(i);
    // Emit the posting_id instead of the index to avoid sync issues with pagination
    const job = this.jobListData[i];
    if (job) {
      this.jobClickEmitter.emit(job.posting_id);
    }
  }

  initialJobLoad(sendTrackEvent: boolean = true) {
    if(this.allJobsData.length) {
      this.currentPage = 1;
      this.jobListData = this.allJobsData.slice(0, this.itemsPerPage);
      this.hasMoreItems = this.jobListData.length < this.allJobsData.length;
      // Only auto-select first job on initial load, not on bookmark updates or other store changes
      if (sendTrackEvent && this.jobListData.length > 0) {
        this.onClickJobItem(0);
      }
    } else {
      this.jobListData = [];
      this.hasMoreItems = false;
    }
  }

  loadMore() {
    // track Load More event
    analytics.track('Job List View More', { section: 'job' });

    const nextPageItems = this.allJobsData.slice(
      this.currentPage * this.itemsPerPage,
      (this.currentPage + 1) * this.itemsPerPage
    );
    this.jobListData = [...this.jobListData, ...nextPageItems];
    this.currentPage++;
    this.hasMoreItems = this.jobListData.length < this.allJobsData.length;
  }

  showErrorMessage(msg: string) {
    this._snackBar.openFromComponent(WarningSnackBarComponent, {
      ...WarningDefaultOptions,
      data: {
        message: msg,
      },
      duration: 3000,
    });
  }

  trackBookmarkClick(value: boolean, jobId: string) {
    const job = this.jobListData.find(j => j.posting_id === jobId);
    if(job) {
      const eventName = value ? 'Job List Bookmark Check' : 'Job List Bookmark Uncheck';
      analytics.track(eventName, {
        section: 'job',
        job_id: job.posting_id,
        job_title: job.job_title,
        employer_name: job.employer_name || null,
      });
    }
  }

  onClickJobBookmark(jobId: string, status: 'ADD' | 'REMOVE') {
    if (this.bookmarkDisabledMap.get(jobId)) {
      return;
    }

    // Find the job index
    const jobIndex = this.allJobsData.findIndex(j => j.posting_id === jobId);
    if (jobIndex === -1) {
      console.error('Job not found in list for ID:', jobId);
      return;
    }

    // Store previous state
    const previousBookmarkState = this.allJobsData[jobIndex].is_bookmarked || false;
    
    // toggle the bookmark state and disable clicks
    const updatedJobs = [...this.allJobsData];
    updatedJobs[jobIndex] = { 
      ...updatedJobs[jobIndex], 
      is_bookmarked: status === 'ADD' 
    };
    this.allJobsData = updatedJobs;
    this.store.dispatch(setJobList({ jobs: updatedJobs }));
    this.bookmarkDisabledMap.set(jobId, true);

    const jobPosting = this.adaptIDJobs.find(
      jobPosting => jobPosting.job.posting_id === jobId
    );

    if (!jobPosting) {
      console.error('Job posting not found for ID:', jobId);
      const revertedJobs = [...this.allJobsData];
      revertedJobs[jobIndex] = { 
        ...revertedJobs[jobIndex], 
        is_bookmarked: previousBookmarkState 
      };
      this.allJobsData = revertedJobs;
      this.store.dispatch(setJobList({ jobs: revertedJobs }));
      this.bookmarkDisabledMap.set(jobId, false);
      return;
    }

    this.jobBookmarkService.addRemoveBookmarkedJob(jobPosting, status).pipe(
      finalize(() => {
        this.bookmarkDisabledMap.set(jobId, false);
      })
    ).subscribe({
      next: (data) => {
        this.trackBookmarkClick(status === 'ADD', jobId);
      },
      error: (error) => {
        //  Revert state on failure
        const revertedJobs = [...this.allJobsData];
        revertedJobs[jobIndex] = { 
          ...revertedJobs[jobIndex], 
          is_bookmarked: previousBookmarkState 
        };
        this.allJobsData = revertedJobs;
        this.store.dispatch(setJobList({ jobs: revertedJobs }));
        this.showErrorMessage('Error while updating bookmark. Please try again!');
      }
    });
  }
}
